import { Ellipsis } from "@visiblethread/react-components";
import Flex, { ALIGN_CLASS, GAP_CLASS } from "components/Flex";

import ActionsCell from "./ActionsCell";
import AssigneeCell from "./AssigneeCell/AssigneeCell";
import CheckboxCell from "./CheckboxCell";
import CommentCell from "./CommentCell";
import { CHECKBOX_COLUMN_WIDTH } from "./constants";
import DocLastUpdatedCell from "./DocLastUpdatedCell/DocLastUpdatedCell";
import SectionLevelCell from "./SectionLevelCell";
import SectionTitleCell from "./SectionTitleCell";
import SelectAllCheckbox from "./SelectAllCheckbox";
import StatusCell from "./StatusCell";
import TraceCell from "./TraceCell";

const STRETCH_STYLE = { flex: 1, textAlign: "center", textIndent: 0 };

export const checkboxColumn = {
  header: <SelectAllCheckbox />,
  size: CHECKBOX_COLUMN_WIDTH,
  id: "selectedRowIds",
  pin: "left",
  cell: ({ row }) => <CheckboxCell row={row} />,
  enableResizing: false,
  meta: { flexGrow: 0 },
};

const traceColumn = (onTrace) => ({
  header: <div style={STRETCH_STYLE}>Trace</div>,
  id: "trace",
  size: 50,
  minSize: 50,
  enableSorting: false,
  cell: ({ row }) => <TraceCell row={row} onTrace={onTrace} />,
  meta: { flexGrow: 0 },
});

const leadingColumns = (getTable, isEditing) => [
  {
    header: <span data-header-id="title">Section Name</span>,
    editKey: "title",
    accessorKey: "title",
    id: "title",
    pin: "left",
    size: 400,
    minSize: 200,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <SectionTitleCell getValue={getValue} row={row} getTable={getTable} />
    ),
    meta: { flexGrow: 0 },
  },
  {
    header: <div style={STRETCH_STYLE}>Level</div>,
    id: "depth",
    cell: ({ row }) => <SectionLevelCell row={row} />,
    size: isEditing ? 102 : 48,
    minSize: isEditing ? 102 : 48,
    meta: { flexGrow: 0 },
  },
];

const statusColumn = (isArchived, onComment) => ({
  header: "Status",
  accessorKey: "status",
  id: "status",
  size: 170,
  minSize: 120,
  enableSorting: false,
  meta: { flexGrow: 0 },
  cell: ({ row }) => (
    <Flex
      justifyContent="space-between"
      alignItems={ALIGN_CLASS.center}
      gap={GAP_CLASS.GAP_200}
      style={{ flex: 1, minWidth: 0 }}
    >
      <StatusCell isArchived={isArchived} row={row} />
      {onComment && <CommentCell row={row} onComment={onComment} />}
    </Flex>
  ),
});

const pageLimitColumn = {
  header: <div style={STRETCH_STYLE}>Pg. Count</div>,
  accessorKey: "pageLimit",
  id: "pageLimit",
  size: 74,
  minSize: 74,
  editKey: "pageLimit",
  enableSorting: false,
  cell: ({ getValue }) => <Ellipsis text={getValue()} style={STRETCH_STYLE} />,
  meta: { flexGrow: 0 },
};

const docLastUpdatedColumn = {
  header: "Doc Last Updated",
  accessorKey: "docLastUpdated",
  id: "docLastUpdated",
  size: 170,
  minSize: 150,
  enableSorting: true,
  sortDescFirst: true,
  meta: { flexGrow: 0 },
  cell: ({ row }) => <DocLastUpdatedCell row={row} />,
};

const notesColumn = {
  header: "Notes",
  accessorKey: "complianceNotes",
  id: "complianceNotes",
  size: 420,
  minSize: 200,
  editKey: "complianceNotes",
  enableSorting: false,
};

const assigneeColumn = {
  header: "Assignee",
  id: "assignee",
  size: 120,
  minSize: 120,
  enableSorting: false,
  cell: ({ row }) => <AssigneeCell row={row} />,
  meta: { flexGrow: 0 },
};

const actionsColumn = (isEditing, getTable) => ({
  header: <span data-header-id="actions" />,
  id: "actions",
  pin: "right",
  size: isEditing ? 60 : 165,
  enableResizing: false,
  enableSorting: false,
  cell: ({ row }) => <ActionsCell getTable={getTable} row={row} />,
  meta: { flexGrow: isEditing ? 1 : 0 },
});

export const createColumns = (
  isArchived,
  onTrace,
  getTable,
  isEditing,
  onComment
) => {
  const [titleColumn, depthColumn] = leadingColumns(getTable, isEditing);

  return [
    titleColumn,
    depthColumn,
    ...(onTrace ? [traceColumn(onTrace)] : []),
    pageLimitColumn,
    ...(isEditing
      ? []
      : [
          statusColumn(isArchived, onComment),
          assigneeColumn,
          docLastUpdatedColumn,
        ]),
    notesColumn,
    ...(isArchived ? [] : [actionsColumn(isEditing, getTable)]),
  ];
};
